import { useMemo } from 'react'
import { Box, Checkbox, FormControl, IconButton, MenuItem, Select, Stack, TextField, Typography } from '@mui/material'
import { Trash2 } from 'lucide-react'
import { useOnboarding } from '../../context/OnboardingContext'
import { licenseDisplayName, licenseNameInputValue, type LicenseRow } from '../../types/onboarding'
import { companyNameOptions } from '../licenseSteps/businessEntityNames'
import { DrlpColumnHeader } from '../licenseSteps/DrlpColumnHeader'
import { LicenseAddMoreButton } from '../licenseSteps/LicenseAddMoreButton'
import { LicenseImportButton } from '../licenseSteps/LicenseImportButton'
import { createEmptyIndividualLicense } from '../licenseSteps/licenseRowFactory'
import { LicenseStatesIntro } from '../licenseSteps/LicenseStatesIntro'
import {
  LicenseTableBody,
  LicenseTableHead,
  LicenseTableRow,
  LicenseTableShell,
  LicenseTableTd,
  LicenseTableTh,
  licenseCellInputSx,
} from '../licenseSteps/LicenseTable'
import { MISC_PRIMARY } from '../../theme/onboardingTheme'

const cellMonoSx = {
  ...(licenseCellInputSx as object),
  '& .MuiOutlinedInput-input': {
    px: 0.5,
    py: 0.25,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontSize: 13,
    '&::placeholder': { color: '#94a3b8', opacity: 1 },
  },
}

export function StepIndividualLicenses() {
  const { state, dispatch } = useOnboarding()

  const individualRows = useMemo(
    () => state.licenses.filter((l) => l.entity === 'Individual'),
    [state.licenses],
  )

  const entityNameOptions = useMemo(
    () =>
      companyNameOptions(
        state.licenses,
        individualRows.map((r) => r.companyName),
      ),
    [state.licenses, individualRows],
  )

  const patch = (id: string, patch: Partial<LicenseRow>) => {
    dispatch({ type: 'UPDATE_LICENSE', id, patch })
  }

  return (
    <Stack spacing={3}>
      <LicenseStatesIntro entityLabel="individuals" />

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Typography component="h3" sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#0f172a' }}>
          Individual Licenses
        </Typography>
        <LicenseImportButton />
      </Box>

      <LicenseTableShell minWidth="1100px">
        <LicenseTableHead>
          <LicenseTableTh sx={{ minWidth: 200 }}>
            Licensee – Full Name as it appears on license
          </LicenseTableTh>
          <LicenseTableTh sx={{ minWidth: 140 }}>Resident SL License State</LicenseTableTh>
          <LicenseTableTh sx={{ minWidth: 140 }}>Resident SL License Number</LicenseTableTh>
          <LicenseTableTh sx={{ minWidth: 160 }}>Company Name</LicenseTableTh>
          <LicenseTableTh sx={{ minWidth: 100 }}>NPN</LicenseTableTh>
          <LicenseTableTh sx={{ width: 96, textAlign: 'center' }}>
            <DrlpColumnHeader />
          </LicenseTableTh>
          <LicenseTableTh sx={{ width: 60, textAlign: 'center' }}>Actions</LicenseTableTh>
        </LicenseTableHead>
        <LicenseTableBody>
          {individualRows.length === 0 ? (
            <LicenseTableRow>
              <LicenseTableTd colSpan={7} sx={{ color: '#64748b' }}>
                <Typography sx={{ display: 'block', py: 2, textAlign: 'center', fontSize: 14 }}>
                  No individuals on file. They will appear here after the license report runs.
                </Typography>
              </LicenseTableTd>
            </LicenseTableRow>
          ) : (
            individualRows.map((row, i) => (
              <LicenseTableRow key={row.licenseeId} striped={i % 2 === 0}>
                <LicenseTableTd>
                  <TextField
                    size="small"
                    sx={licenseCellInputSx}
                    value={licenseNameInputValue(row)}
                    onChange={(e) =>
                      patch(row.licenseeId, {
                        licenseeFirstName: '',
                        licenseeLastName: e.target.value,
                      })
                    }
                    placeholder="e.g. Jane Smith"
                    inputProps={{ 'aria-label': 'Licensee full name' }}
                  />
                </LicenseTableTd>
                <LicenseTableTd>
                  <TextField
                    size="small"
                    sx={{
                      ...(licenseCellInputSx as object),
                      '& .MuiOutlinedInput-input': { textTransform: 'uppercase' },
                    }}
                    value={row.licenseState.toUpperCase()}
                    inputProps={{ maxLength: 2, 'aria-label': 'Resident SL license state' }}
                    onChange={(e) => {
                      const code = e.target.value.trim().slice(0, 2).toUpperCase()
                      patch(row.licenseeId, { licenseState: code })
                    }}
                    placeholder="e.g. CA"
                  />
                </LicenseTableTd>
                <LicenseTableTd>
                  <TextField
                    size="small"
                    sx={cellMonoSx}
                    value={row.licenseNumber}
                    onChange={(e) => patch(row.licenseeId, { licenseNumber: e.target.value })}
                    inputProps={{ 'aria-label': 'Resident SL license number' }}
                  />
                </LicenseTableTd>
                <LicenseTableTd>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={row.companyName ?? ''}
                      onChange={(e) => patch(row.licenseeId, { companyName: e.target.value })}
                      inputProps={{ 'aria-label': 'Company name' }}
                      disabled={entityNameOptions.length === 0}
                      displayEmpty
                      sx={{
                        fontSize: 14,
                        bgcolor: 'transparent',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(37, 99, 235, 0.3)',
                        },
                      }}
                    >
                      <MenuItem value="">
                        {entityNameOptions.length === 0
                          ? 'Add entities on Business Licenses first'
                          : 'Select entity…'}
                      </MenuItem>
                      {entityNameOptions.map((name) => (
                        <MenuItem key={name} value={name}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </LicenseTableTd>
                <LicenseTableTd>
                  <TextField
                    size="small"
                    sx={cellMonoSx}
                    value={row.npn}
                    onChange={(e) => patch(row.licenseeId, { npn: e.target.value })}
                    inputProps={{ 'aria-label': 'NPN' }}
                  />
                </LicenseTableTd>
                <LicenseTableTd sx={{ textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Checkbox
                      checked={row.isDrlp}
                      onChange={() => dispatch({ type: 'SET_DRLP', licenseeId: row.licenseeId })}
                      size="small"
                      sx={{
                        p: 0,
                        color: '#cbd5e1',
                        '&.Mui-checked': { color: MISC_PRIMARY },
                      }}
                      inputProps={{
                        'aria-label': `DRLP for ${licenseDisplayName(row)} in ${row.licenseState}`,
                      }}
                    />
                  </Box>
                </LicenseTableTd>
                <LicenseTableTd sx={{ textAlign: 'center' }}>
                  <IconButton
                    size="small"
                    onClick={() => dispatch({ type: 'REMOVE_LICENSE', licenseeId: row.licenseeId })}
                    sx={{
                      color: '#64748b',
                      '&:hover': { color: '#dc2626', backgroundColor: '#fee2e2' },
                    }}
                    aria-label={`Delete ${licenseDisplayName(row)}`}
                  >
                    <Trash2 size={16} />
                  </IconButton>
                </LicenseTableTd>
              </LicenseTableRow>
            ))
          )}
        </LicenseTableBody>
      </LicenseTableShell>

      <LicenseAddMoreButton createRow={createEmptyIndividualLicense} />
    </Stack>
  )
}
